/**
 * useFreshRecruitingNoteUrls
 *
 * Keeps a RecruitingNote's signed image/drawing URLs fresh for viewers.
 * Signed URLs expire after 60 minutes server-side; without this hook, a page
 * left open would silently render broken images.
 *
 * Strategy:
 *  1. Pre-emptive refresh: if `imageUrlExpiresAt` / `drawingDataUrlExpiresAt`
 *     is within {@link EXPIRY_BUFFER_MS} (5 minutes) or already past, refetch
 *     the note before rendering. Matches the shape of `useProspectVideos`.
 *  2. Recovery refresh: the caller invokes `handleImageError` on `<img
 *     onError>`; we refetch once. A second consecutive error flips
 *     `isUnavailable` so the caller can render a placeholder.
 *
 * Pre-Phase-3 API responses may omit the expiry fields — in that case the
 * pre-emptive path is skipped and we rely entirely on the onError recovery.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { refetchRecruitingNote } from '../services/recruiting-notes';
import type { RecruitingNoteResponse } from '../types/api';

const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/**
 * `null` means "not applicable" (e.g. expiry field is absent from the DTO —
 * pre-Phase-3 — so we can't tell if the URL is stale).
 */
const msUntilExpiry = (expiresAt: string | null | undefined): number | null => {
  if (!expiresAt) return null;
  const parsed = new Date(expiresAt).getTime();
  if (Number.isNaN(parsed)) return null;
  return parsed - Date.now();
};

const isAboutToExpire = (expiresAt: string | null | undefined): boolean => {
  const ms = msUntilExpiry(expiresAt);
  if (ms === null) return false;
  return ms < EXPIRY_BUFFER_MS;
};

interface UseFreshRecruitingNoteUrlsResult {
  imageUrl: string | null;
  drawingDataUrl: string | null;
  /**
   * `true` once we've consumed our one retry after an `<img>` load error and
   * still can't display the image — callers should render a placeholder.
   */
  isUnavailable: boolean;
  /** Attach to `<img onError>`. Triggers one refetch; subsequent errors stick. */
  handleImageError: () => void;
}

/**
 * Returns the freshest available signed URLs for a recruiting note.
 *
 * Handles both the pre-emptive refresh path (expiry timestamp known and near)
 * and the recovery path (browser reports `<img>` load failure).
 */
export function useFreshRecruitingNoteUrls(
  note: RecruitingNoteResponse,
): UseFreshRecruitingNoteUrlsResult {
  const [currentNote, setCurrentNote] = useState<RecruitingNoteResponse>(note);
  const [isUnavailable, setIsUnavailable] = useState(false);

  // Tracks whether the current rendered URL has already been retried once, so
  // a broken image can't trigger an infinite refetch loop.
  const hasRetriedRef = useRef(false);
  // In-flight refetch guard — pre-emptive effect and onError handler should
  // not fire parallel requests for the same note.
  const isRefetchingRef = useRef(false);
  // Remembers which expiry we've already pre-emptively refreshed against, so
  // a server that keeps returning a near-expired URL doesn't cause us to
  // hammer it in a loop. Keyed by `${id}:${imageUrlExpiresAt}` etc.
  const preemptivelyRefreshedKeyRef = useRef<string | null>(null);

  // Re-sync when the parent passes a different note (new id) or a meaningfully
  // updated version of the same note (new `updatedAt`). We intentionally do
  // NOT re-sync on every prop reference change — otherwise a parent re-render
  // would clobber URLs we just refetched internally.
  const parentVersionKey = `${note.id}:${note.updatedAt ?? note.createdAt}`;
  const syncedVersionKeyRef = useRef(parentVersionKey);
  useEffect(() => {
    if (syncedVersionKeyRef.current !== parentVersionKey) {
      syncedVersionKeyRef.current = parentVersionKey;
      hasRetriedRef.current = false;
      preemptivelyRefreshedKeyRef.current = null;
      setIsUnavailable(false);
      setCurrentNote(note);
    }
  }, [parentVersionKey, note]);

  const refetch = useCallback(async () => {
    if (isRefetchingRef.current) return;
    isRefetchingRef.current = true;
    try {
      const fresh = await refetchRecruitingNote(currentNote.id);
      setCurrentNote(fresh);
      // Fresh URLs — reset retry so a later expiry can trigger another one.
      hasRetriedRef.current = false;
      setIsUnavailable(false);
    } catch (err) {
      console.error('[useFreshRecruitingNoteUrls] Failed to refetch note', {
        error: err,
        noteId: currentNote.id,
      });
    } finally {
      isRefetchingRef.current = false;
    }
  }, [currentNote.id]);

  // Pre-emptive refresh when expiry is within the buffer window.
  const shouldPreemptivelyRefresh = useMemo(() => {
    return (
      isAboutToExpire(currentNote.imageUrlExpiresAt) ||
      isAboutToExpire(currentNote.drawingDataUrlExpiresAt)
    );
  }, [currentNote.imageUrlExpiresAt, currentNote.drawingDataUrlExpiresAt]);

  useEffect(() => {
    if (!shouldPreemptivelyRefresh) return;
    // Guard against server returning another near-expired URL in a loop.
    const key = `${currentNote.id}:${currentNote.imageUrlExpiresAt ?? ''}:${currentNote.drawingDataUrlExpiresAt ?? ''}`;
    if (preemptivelyRefreshedKeyRef.current === key) return;
    preemptivelyRefreshedKeyRef.current = key;
    refetch();
  }, [
    shouldPreemptivelyRefresh,
    refetch,
    currentNote.id,
    currentNote.imageUrlExpiresAt,
    currentNote.drawingDataUrlExpiresAt,
  ]);

  const handleImageError = useCallback(() => {
    if (hasRetriedRef.current) {
      // Already burned our one retry — surface as unavailable.
      setIsUnavailable(true);
      return;
    }
    hasRetriedRef.current = true;
    refetch();
  }, [refetch]);

  return {
    imageUrl: currentNote.imageUrl,
    drawingDataUrl: currentNote.drawingDataUrl,
    isUnavailable,
    handleImageError,
  };
}
